<script>
import { defineAsyncComponent, ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import PrimaryNav from 'dashboard/components-next/sidebar/PrimaryNav.vue';
import TopBar from 'dashboard/components-next/TopBar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStorage, useWindowSize } from '@vueuse/core';
import { detectCategoryFromRoute } from 'dashboard/helper/routeCategory';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import MobileFooterNav from 'dashboard/components-next/sidebar/MobileFooterNav.vue';

export default {
  components: {
    NextSidebar,
    PrimaryNav,
    TopBar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    MobileSidebarLauncher,
    MobileFooterNav,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const route = useRoute();
    const isSecondarySidebarCollapsed = useStorage(
      'wlab-secondary-sidebar-collapsed',
      false
    );
    const forceDesktopProfileView = useStorage(
      LOCAL_STORAGE_KEYS.PROFILE_FORCE_DESKTOP_VIEW,
      false
    );

    // Active category state (initialize synchronously to avoid one-frame mismatch)
    const activeCategory = ref(detectCategoryFromRoute(route, ''));

    // Watch route changes to keep category in sync
    watch(
      () => [route.name, route.path],
      () => {
        activeCategory.value = detectCategoryFromRoute(route, '');
      }
    );

    const normalizedActiveCategory = computed(() =>
      String(activeCategory.value || '').toLowerCase()
    );
    const isInboxViewRoute = computed(() =>
      ['inbox_view', 'inbox_view_conversation'].includes(
        String(route.name || '')
      )
    );
    const isArchivesRoute = computed(() =>
      ['archives_view', 'archives_view_conversation'].includes(
        String(route.name || '')
      )
    );
    const usesInboxStyleLayout = computed(
      () => isInboxViewRoute.value || Boolean(route?.meta?.useInboxPageLayout)
    );
    const shouldHideSecondarySidebar = computed(() =>
      Boolean(route?.meta?.hideSecondarySidebar)
    );
    const isWideSecondarySidebarCategory = computed(() =>
      [
        'reports',
        'campaigns',
        'qadashboard',
        'settings',
        'contacts',
        'ailab',
        'workforce',
      ].includes(normalizedActiveCategory.value)
    );
    const secondarySidebarWidth = computed(() => {
      if (isSecondarySidebarCollapsed.value) return 0;
      return isWideSecondarySidebarCategory.value ? 296 : 200;
    });
    const floatingSidebarToggleStyle = computed(() => {
      const primaryNavWidth = 48;
      const left = isSecondarySidebarCollapsed.value
        ? primaryNavWidth + 8
        : primaryNavWidth + secondarySidebarWidth.value - 12;
      return {
        left: `${left}px`,
        bottom: '18px',
      };
    });
    const toggleSecondarySidebar = () => {
      isSecondarySidebarCollapsed.value = !isSecondarySidebarCollapsed.value;
    };

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      activeCategory,
      isInboxViewRoute,
      isArchivesRoute,
      usesInboxStyleLayout,
      shouldHideSecondarySidebar,
      isSecondarySidebarCollapsed,
      forceDesktopProfileView,
      floatingSidebarToggleStyle,
      toggleSecondarySidebar,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    isProfileRoute() {
      return String(this.$route.name || '').startsWith('profile_settings');
    },
    showMobileStandaloneProfile() {
      return (
        this.isProfileRoute &&
        this.isSmallScreen &&
        !this.forceDesktopProfileView
      );
    },
    showMobileFooterNav() {
      if (this.usesInboxStyleLayout || !this.isSmallScreen) {
        return false;
      }

      return (
        ['Conversation', 'Engage', 'TeamManagement'].includes(
          this.activeCategory
        ) || this.isProfileRoute
      );
    },
    contentWrapperStyle() {
      if (this.showMobileFooterNav) {
        return {
          backgroundColor: 'var(--wlab-bg-sidebar)',
          padding: '0',
        };
      }

      if (this.isArchivesRoute) {
        return {
          backgroundColor: 'var(--wlab-bg-sidebar)',
          padding: '0 12px 0 0',
        };
      }

      if (this.usesInboxStyleLayout) {
        return {
          backgroundColor: 'var(--wlab-bg-sidebar)',
          padding: '0 12px 12px 12px',
        };
      }

      return {
        backgroundColor: 'var(--wlab-bg-sidebar)',
        padding: '0 12px 12px 0px',
      };
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
    handleCategorySelect(categoryName) {
      this.activeCategory = categoryName;
    },
  },
};
</script>

<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <!-- Primary Navigation Container (maintains fixed width in layout) -->
    <div
      class="primary-nav-container hidden md:block relative flex-shrink-0"
      style="width: 48px; min-width: 48px"
    >
      <!-- Primary Navigation (Left Icon Bar) - expands on hover as overlay -->
      <PrimaryNav
        :active-category="activeCategory"
        class="flex"
        @select="handleCategorySelect"
        @open-key-shortcut-modal="toggleKeyShortcutModal"
        @show-create-account-modal="openCreateAccountModal"
      />
    </div>

    <!-- Right Side: TopBar + Sidebar + Content -->
    <div
      class="flex flex-col flex-1 h-full overflow-hidden"
      style="background-color: var(--wlab-bg-sidebar)"
    >
      <!-- Top Bar with Account Switcher -->
      <TopBar
        v-if="!showMobileStandaloneProfile"
        class="flex-shrink-0"
        @show-create-account-modal="openCreateAccountModal"
      />

      <!-- Below TopBar: Sidebar + Content -->
      <div class="flex flex-1 min-h-0 overflow-hidden">
        <!-- Secondary Navigation (Submenu Sidebar) -->
        <NextSidebar
          v-if="
            !showMobileFooterNav &&
            !usesInboxStyleLayout &&
            !shouldHideSecondarySidebar &&
            (isSmallScreen || !isSecondarySidebarCollapsed)
          "
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          :active-category="activeCategory"
          :show-header="false"
          @toggle-account-modal="toggleAccountModal"
          @open-key-shortcut-modal="toggleKeyShortcutModal"
          @close-key-shortcut-modal="closeKeyShortcutModal"
          @show-create-account-modal="openCreateAccountModal"
          @close-mobile-sidebar="closeMobileSidebar"
        />

        <!-- Floating collapse/expand button (desktop only) -->
        <button
          v-if="
            !usesInboxStyleLayout &&
            !shouldHideSecondarySidebar &&
            !isSmallScreen
          "
          type="button"
          class="fixed z-50 flex items-center gap-2 px-4 py-2.5 rounded-full shadow-xl border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-slate-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand transition-colors duration-200"
          :style="floatingSidebarToggleStyle"
          :aria-label="
            isSecondarySidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
          "
          @click="toggleSecondarySidebar"
        >
          <span
            class="flex-shrink-0 size-5"
            :class="
              isSecondarySidebarCollapsed
                ? 'i-lucide-chevron-right'
                : 'i-lucide-chevron-left'
            "
          />
          <span class="text-sm font-semibold tracking-wide">
            {{ isSecondarySidebarCollapsed ? 'Expand' : 'Collapse' }}
          </span>
        </button>

        <!-- Content Wrapper with padding -->
        <div
          class="flex flex-1 h-full min-h-0 overflow-hidden"
          :style="contentWrapperStyle"
        >
          <main
            class="flex flex-1 h-full w-full min-h-0 overflow-hidden"
            :class="
              showMobileFooterNav
                ? 'rounded-none md:rounded-2xl'
                : 'rounded-2xl'
            "
            style="background-color: var(--wlab-bg-primary)"
          >
            <UpgradePage
              v-show="showUpgradePage"
              ref="upgradePageRef"
              :bypass-upgrade-page="bypassUpgradePage"
            >
              <MobileSidebarLauncher
                v-if="!showMobileFooterNav && !usesInboxStyleLayout"
                :is-mobile-sidebar-open="isMobileSidebarOpen"
                @toggle="toggleMobileSidebar"
              />
            </UpgradePage>
            <template v-if="!showUpgradePage">
              <router-view />
              <CommandBar />
              <CopilotLauncher />
              <MobileSidebarLauncher
                v-if="!showMobileFooterNav && !usesInboxStyleLayout"
                :is-mobile-sidebar-open="isMobileSidebarOpen"
                @toggle="toggleMobileSidebar"
              />
              <CopilotContainer />
            </template>
            <AddAccountModal
              :show="showCreateAccountModal"
              @close-account-create-modal="closeCreateAccountModal"
            />
            <WootKeyShortcutModal
              v-model:show="showShortcutModal"
              @close="closeKeyShortcutModal"
              @clickaway="closeKeyShortcutModal"
            />
          </main>
        </div>
      </div>
      <MobileFooterNav v-if="showMobileFooterNav" />
    </div>
  </div>
</template>
